import { useState } from 'react';
import type { ReportFiltersState, AccountInfo, CardInfo } from '../../../hooks/useReports';

interface PayeeInfo { id: string; name: string; }

interface Props {
  filters: ReportFiltersState;
  onChange: (f: ReportFiltersState) => void;
  isPremium: boolean;
  categories: string[];
  accounts: AccountInfo[];
  cards: CardInfo[];
  payees?: PayeeInfo[];
}

const PERIODS = [
  { value: '1m', label: 'Este mês' },
  { value: '2m', label: '2 meses' },
  { value: '3m', label: '3 meses' },
  { value: '6m', label: '6 meses', premium: true },
  { value: '12m', label: '12 meses', premium: true },
  { value: 'custom', label: 'Personalizado' },
];

const RECURRENCE_LABELS: Record<string, string> = {
  monthly: 'Mensal', weekly: 'Semanal', annual: 'Anual', daily: 'Diário', biweekly: 'Quinzenal',
};

export const ReportFilters = ({ filters, onChange, isPremium, categories, accounts, cards, payees = [] }: Props) => {
  const [showAdv, setShowAdv] = useState(false);

  const set = (patch: Partial<ReportFiltersState>) => onChange({ ...filters, ...patch });

  const hasActiveFilters = filters.tipo !== 'todos' || filters.origin !== 'todos' ||
    filters.categoria !== '' || filters.status !== 'todos' || filters.recorrente !== 'todos' ||
    filters.parcelado !== 'todos' || filters.accountId !== '' || filters.cardId !== '' || filters.payeeId !== '';

  const clearAll = () => onChange({
    ...filters, tipo: 'todos', origin: 'todos', accountId: '', cardId: '',
    categoria: '', payeeId: '', status: 'todos', recorrente: 'todos', parcelado: 'todos',
  });

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 space-y-3">
      {/* Period row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-gray-400 font-600 uppercase tracking-wider mr-1">Período</span>
        {PERIODS.map(p => {
          const locked = (p as any).premium && !isPremium;
          return (
            <button
              key={p.value}
              onClick={() => !locked && set({ period: p.value as any })}
              disabled={locked}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-all whitespace-nowrap ${
                filters.period === p.value ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${locked ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
            >
              {p.label}{locked && <i className="ri-lock-line text-[10px] ml-1" />}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2">
          {hasActiveFilters && (
            <button onClick={clearAll} className="text-[11px] text-red-500 hover:text-red-600 font-600 cursor-pointer whitespace-nowrap flex items-center gap-1">
              <i className="ri-close-circle-line" />Limpar filtros
            </button>
          )}
          <button
            onClick={() => setShowAdv(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-600 cursor-pointer whitespace-nowrap transition-colors ${
              showAdv || hasActiveFilters ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className="ri-equalizer-line" />
            Filtros avançados
            {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />}
            <i className={`ri-arrow-${showAdv ? 'up' : 'down'}-s-line text-[13px]`} />
          </button>
        </div>
      </div>

      {/* Custom date range */}
      {filters.period === 'custom' && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[11px] text-gray-400 font-600">De:</span>
          <input
            type="date" value={filters.customStart}
            onChange={e => set({ customStart: e.target.value })}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:border-emerald-400 cursor-pointer"
          />
          <span className="text-[11px] text-gray-400 font-600">Até:</span>
          <input
            type="date" value={filters.customEnd}
            onChange={e => set({ customEnd: e.target.value })}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 focus:outline-none focus:border-emerald-400 cursor-pointer"
          />
        </div>
      )}

      {/* Advanced filters */}
      {showAdv && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 border-t border-gray-50">
          {/* Tipo */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Tipo</label>
            <select value={filters.tipo} onChange={e => set({ tipo: e.target.value as any })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="todos">Receitas e Despesas</option>
              <option value="receita">Apenas Receitas</option>
              <option value="despesa">Apenas Despesas</option>
            </select>
          </div>

          {/* Origem */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Origem</label>
            <select value={filters.origin} onChange={e => set({ origin: e.target.value as any, accountId: '', cardId: '' })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="todos">Contas e Cartões</option>
              <option value="conta">Apenas Contas</option>
              <option value="cartao">Apenas Cartões</option>
            </select>
          </div>

          {/* Conta específica */}
          {(filters.origin === 'todos' || filters.origin === 'conta') && accounts.length > 0 && (
            <div>
              <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Conta</label>
              <select value={filters.accountId} onChange={e => set({ accountId: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
                <option value="">Todas as contas</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {/* Cartão específico */}
          {(filters.origin === 'todos' || filters.origin === 'cartao') && cards.length > 0 && (
            <div>
              <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Cartão</label>
              <select value={filters.cardId} onChange={e => set({ cardId: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
                <option value="">Todos os cartões</option>
                {cards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          {/* Categoria */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Categoria</label>
            <select value={filters.categoria} onChange={e => set({ categoria: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="">Todas as categorias</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Favorecido */}
          {payees.length > 0 && (
            <div>
              <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Favorecido</label>
              <select value={filters.payeeId} onChange={e => set({ payeeId: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
                <option value="">Todos os favorecidos</option>
                {payees.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Status</label>
            <select value={filters.status} onChange={e => set({ status: e.target.value as any })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="todos">Todos os status</option>
              <option value="pago">Pago</option>
              <option value="pendente">Pendente</option>
              <option value="vencido">Vencido / Atrasado</option>
            </select>
          </div>

          {/* Recorrente */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Recorrência</label>
            <select value={filters.recorrente} onChange={e => set({ recorrente: e.target.value as any })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="todos">Todos</option>
              <option value="sim">Apenas recorrentes</option>
              <option value="nao">Apenas avulsos</option>
            </select>
          </div>

          {/* Parcelado */}
          <div>
            <label className="text-[10px] text-gray-400 font-600 uppercase tracking-wider block mb-1">Parcelamento</label>
            <select value={filters.parcelado} onChange={e => set({ parcelado: e.target.value as any })}
              className="w-full border border-gray-200 rounded-xl px-3 py-1.5 text-[12px] text-gray-700 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer">
              <option value="todos">Todos</option>
              <option value="sim">Apenas parcelados</option>
              <option value="nao">Apenas à vista</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
